import Vue from 'vue'
import { createClientApp } from './app-client'
import { App as NApp } from '@capacitor/app';
import { Device } from '@capacitor/device';
import { Haptics, NotificationType } from '@capacitor/haptics';
import { PushNotifications } from '@capacitor/push-notifications';
import { FirebaseAnalytics } from '@capacitor-community/firebase-analytics';
import i18n from './utils/i18n.js'
import { storeTokenInSecureStorage, handleGoogleStatus } from './utils/ApiMiddleware.js';
import { GlobalEventEmitter } from './utils/GlobalEventEmitter.js';
import {
  AppTrackingTransparency,
} from 'capacitor-plugin-app-tracking-transparency';

Vue.config.productionTip = false
const TARGETS = Vue.observable({
  icons: {}
});

const FEATURES = Vue.observable({
  settings: {},
});

Vue.prototype.$FEATURES = FEATURES;

Vue.prototype.$TARGETS = TARGETS;
Vue.prototype.$TRIAL_DAYS = 14;
Vue.prototype.$IS_APP = window.navigator.userAgent.includes('TourialApp');
Vue.prototype.$STRIPE_PK = 'pk_live_51N2adtANRLRb2OclbphcXmypySTaQjwPZTJF1k4HxYN4on8n3GVvd4GZWDyyQmKBJLEgh5FKVLpI7t3SCbwGsBXb00uVQty7mv';

if(!Vue.prototype.$IS_APP && window.location.hostname != 'www.tourial.it' && window.location.hostname != 'tourial.it'){
  Vue.prototype.$STRIPE_PK = 'pk_test_51N2adtANRLRb2OcljbTnbwXPmwXdLJn2hCex3YqEsECrDGmWZiWBgxhRe6iA79wEecmREtcfuchveTUU6jVbcAM300OH8TVBSC';
}

// OVERRIDE FOR TESTING
//Vue.prototype.$STRIPE_PK = 'pk_test_51N2adtANRLRb2OcljbTnbwXPmwXdLJn2hCex3YqEsECrDGmWZiWBgxhRe6iA79wEecmREtcfuchveTUU6jVbcAM300OH8TVBSC';

import * as Sentry from "@sentry/vue";

import VueNumber from "vue-number-animation";
Vue.use(VueNumber);

import 'lightgallery.js'
import 'lightgallery.js/dist/css/lightgallery.css'
import Vuesax from 'vuesax'

import 'vuesax/dist/vuesax.css' //Vuesax styles
Vue.use(Vuesax, {
  // options here
  colors: {
    primary:'#EF7E18',
    dark:'rgba(0, 0, 0, 0.1)'
  }
})

// WRAP $vs.notification to add haptic feedback on mobile
const originalNotify = Vue.prototype.$vs.notification;
Vue.prototype.$vs.notification = function (options) {
  originalNotify(options);
  if (Vue.prototype.$IS_APP) {
    let type = NotificationType.Success;
    if (options.color == 'danger') {
      type = NotificationType.Error;
    }
    Haptics.notification({
      type,
    });
  }
};

// Helper to convert to PascalCase
function toPascalCase(str) {
  return (str || '')
    .replace(/(^\w|-\w)/g, clear => clear.replace(/-/, '').toUpperCase());
}

import VueSlideoutPanel from 'vue2-slideout-panel';
Vue.use(VueSlideoutPanel);

// Wrapper for $showPanel to track opened panels
const openPanels = [];
const originalShowPanel = Vue.prototype.$showPanel;

Vue.prototype.$showPanel = function(options) {
  // Call the original $showPanel
  const result = originalShowPanel.call(this, options);
  
  openPanels.push(result);
  
  return result;
};

// Add a method to close all panels
Vue.prototype.$closeAllPanels = async function() {
  if(openPanels.length === 0) return;

  const promises = openPanels.map(panel => panel.hide());
  await Promise.all(promises);

  await new Promise(resolve => setTimeout(resolve, 500)); // wait for panels to close and animations to finish
};

import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css';

Vue.component('v-select', vSelect)

import 'boxicons/css/boxicons.min.css'
// Router is now created via the client app factory
const { app: vueApp, router: vRouter } = createClientApp({})

const SentrySettings = {
  Vue,
  dsn: "https://3a0f76a384b9efbf406f1b4d41ff95e4@sentry.wised.it/3",
  environment: window.configuration.IS_TEST ? 'TEST' : 'PROD', // production, test
  integrations: [
    Sentry.browserTracingIntegration({ router: vRouter }),
    Sentry.replayIntegration({
      networkDetailAllowUrls: window.configuration.IS_TEST ? [window.location.origin] : [], // Allow all network requests to be captured
    })
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [window.location.origin],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
};
if(!Vue.prototype.$IS_APP){
  Sentry.init(SentrySettings);
} else {
  (async () => {
    try {
      // Step 1: Get current ATT status
      let { status } = await AppTrackingTransparency.getStatus();

      // Step 2: If notDetermined, request permission
      if (status === 'notDetermined') {
        const response = await AppTrackingTransparency.requestPermission();
        status = response.status;
      }

      // Step 3: Handle all status cases
      if (status !== 'authorized') {
        window.gtag('consent', 'default', {
          ad_storage: 'denied',
          ad_user_data: 'denied',
          ad_personalization: 'denied',
          analytics_storage: 'denied',
          functionality_storage: 'denied',
          personalization_storage: 'denied',
          security_storage: 'denied',
        });
        console.warn('[ATT] Tracking not authorized. Status:', status);
        return;
      }

      window.gtag('consent', 'update', {
        analytics_storage: 'granted',
        functionality_storage: 'granted',
        personalization_storage: 'granted',
      });
      Sentry.init(SentrySettings);

      // Platform-specific Firebase Analytics collection control
      try {
        const { Capacitor } = await import('@capacitor/core');
        const platform = Capacitor.getPlatform();

        if (platform === 'android') {
          await FirebaseAnalytics.setCollectionEnabled({ enabled: true });
        }

        if (platform === 'ios') {
          if (status === 'authorized') {
            await FirebaseAnalytics.setCollectionEnabled({ enabled: true });
          }
        }

        // Initialize Firebase Analytics and log a sample event
        await FirebaseAnalytics.logEvent({
          name: 'app_start',
          params: { platform: 'capacitor' }
        });
        // Optionally set a user ID
        // await FirebaseAnalytics.setUserId({ userId: 'user_id_here' });
      } catch (e) {
        console.warn('Firebase Analytics error:', e);
      }
    } catch (err) {
      console.error('[ATT] Error during App Tracking Transparency flow:', err);
      window.gtag('consent', 'default', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'denied',
        functionality_storage: 'denied',
        personalization_storage: 'denied',
        security_storage: 'denied',
      });
    }
  })();
}

NApp.addListener('appUrlOpen', async (data) => {
  GlobalEventEmitter.$emit('appUrlOpen', data);

  if (data.url.includes('tourial://')) {
    // get query params
    let url = new URL(data.url);
    let params = url.searchParams;

    // get token
    let token = params.get('token');
    if(token){
      await storeTokenInSecureStorage(token);
      GlobalEventEmitter.$emit('loadUserMeta');
    }

    let status = params.get('status');
    if(status){
      handleGoogleStatus(status);
    }
  }
});
/**
 * Handle app opening via push notification tap.
 * - pushNotificationActionPerformed: user taps notification when app is in background/foreground
 * - appRestoredResult: app is COLD-STARTED by tapping a notification (data available via App plugin)
 */
if (Vue.prototype.$IS_APP) {
  // Tapped notification while app is running/backgrounded
  PushNotifications.addListener('pushNotificationActionPerformed', (event) => {
    let payload = {};
    try {
      payload =
        (event && event.notification && event.notification.data) || {};
    } catch (e) {
      console.warn('Error reading push notification data', e);
    }

    // Handle CTA navigation when user taps the notification while app is running/backgrounded
    try {
      const ctaType = payload.call_to_action_type || payload.callToActionType || payload.cta_type;
      const ctaValue = payload.call_to_action_value || payload.cal_to_action_value || payload.path || payload.url;
      if (ctaType === 'URL' && typeof ctaValue === 'string' && ctaValue.length) {
        let path = ctaValue;
        if (/^https?:\/\//i.test(ctaValue)) {
          try {
            const u = new URL(ctaValue);
            path = `${u.pathname}${u.search}${u.hash}`;
          } catch (err) {
            console.warn('Invalid URL in CTA value; falling back to raw value');
          }
        }
        vRouter.push(path).catch(err => { if (err && err.name !== 'NavigationDuplicated') { console.debug('Router push error (tap):', err); } });
      }
    } catch (navErr) {
      console.warn('CTA navigation error (tap):', navErr);
    }

    try {
      GlobalEventEmitter.$emit('pushNotificationActionPerformed', event);
    } catch (_) { console.debug('pushNotificationActionPerformed emit failed'); }
  });

  // App cold-started from a push notification tap
  NApp.addListener('appRestoredResult', (result) => {
    if (result && result.pluginId === 'PushNotifications') {
      const dataObj = (result.data && (result.data.notification && result.data.notification.data)) ||
                      (result.data && result.data.data) ||
                      result.data ||
                      {};

      // Handle CTA navigation when app is cold-started from notification tap
      try {
        const ctaType = dataObj.call_to_action_type || dataObj.callToActionType || dataObj.cta_type;
        const ctaValue = dataObj.call_to_action_value || dataObj.cal_to_action_value || dataObj.path || dataObj.url;
        if (ctaType === 'URL' && typeof ctaValue === 'string' && ctaValue.length) {
          let path = ctaValue;
          if (/^https?:\/\//i.test(ctaValue)) {
            try {
              const u = new URL(ctaValue);
              path = `${u.pathname}${u.search}${u.hash}`;
            } catch (err) {
              console.warn('Invalid URL in CTA value; falling back to raw value');
            }
          }
          // Ensure router is ready on cold start
          Vue.nextTick(() => {
            vRouter.push(path).catch(err => { if (err && err.name !== 'NavigationDuplicated') { console.debug('Router push error (cold start):', err); } });
          });
        }
      } catch (navErr) {
        console.warn('CTA navigation error (cold start):', navErr);
      }

      try {
        GlobalEventEmitter.$emit('pushNotificationActionPerformed', result.data);
      } catch (_) { console.debug('appRestoredResult emit failed'); }
    }
  });
}

// on device ready call initPurchase
document.addEventListener('deviceready', () => {
  const { initPurchase } = require('./utils/inAppPurchase.js');
  initPurchase();
});
// global vue variable for navigation history
Vue.prototype.$NAVIGATION_HISTORY = new Array();
const DEFAULT_TITLE = 'Tourial';

function hasQueryParams(route) {
  return !!Object.keys(route.query).length
}


vRouter.beforeEach(async (to, from, next) => {
  if (to.path.startsWith('/blog') && !Vue.prototype.$IS_APP) {
    window.location.href = to.fullPath
    return
  }

  await Vue.prototype.$closeAllPanels();

  try { GlobalEventEmitter.$emit('aiPanelForceClose'); } catch (e) {console.debug('aiPanelForceClose emit failed');}
   // if from name equals 'Viaggio' and to name equals 'Scheda del luogo', override to meta params 'menu' with 'Viaggi', otherwise set it to 'Esplora'
  if(to.name == 'Scheda del luogo'){
    if(from.name == 'Viaggio'){
      to.meta.menu = 'Viaggi';
    } else {
      to.meta.menu = 'Esplora';
    }
  }


   if(!hasQueryParams(to) && hasQueryParams(from)){
    if(from.query.status == 'null'){
      delete from.query.status;
    }

    if(from.query.check_subscription == 'null'){
      delete from.query.check_subscription;
    }

    if(from.query.payment_intent_client_secret == 'null'){
      delete from.query.payment_intent_client_secret;
    }
    next({ ...to, query: from.query });
  } else {
    next()
  }
})

vRouter.afterEach((to, from) => {
  Vue.nextTick(() => {
      document.title = to.meta.title != undefined ? (DEFAULT_TITLE + ' | ' + to.meta.title) : DEFAULT_TITLE;
  });
  // detect navigation back
  if (Vue.prototype.$NAVIGATION_HISTORY.length > 0 && Vue.prototype.$NAVIGATION_HISTORY[Vue.prototype.$NAVIGATION_HISTORY.length - 1].fullPath == to.fullPath) {
    Vue.prototype.$NAVIGATION_HISTORY.pop();
  } else {
    Vue.prototype.$NAVIGATION_HISTORY.push(from);
  }

  if(to.meta.menu != from.meta.menu && !['HomePage', 'Preferiti'].includes(from.meta.menu)){
    // reset navigation history
    Vue.prototype.$NAVIGATION_HISTORY = new Array();
  }

  // Set Firebase Analytics screen name on navigation (native app only)
  if (Vue.prototype.$IS_APP) {
    
    const screenName = to.name || 'Unknown';
    const nameOverride = toPascalCase(to.name || screenName);
    (async () => {
      try {
        await FirebaseAnalytics.setScreenName({
          screenName,
          nameOverride,
        });
      } catch (e) {
        // Ignore errors in analytics
      }
    })();
  }
});




import "@fontsource/montserrat"; // Defaults to weight 400.
import "@fontsource/montserrat/500.css"; // Weight 500.
import "@fontsource/montserrat/600.css"; // Weight 500.
import "@fontsource/montserrat/700.css"; // Weight 500.
import "@fontsource/montserrat/800.css"; // Weight 500.


import GAuth from 'vue-google-oauth2'
const gauthOption = {
  clientId: '34325553753-dk8esvk9sda8bd2ev3m1a4721adccldl.apps.googleusercontent.com',
  scope: 'profile email',
  prompt: 'select_account'
}
Vue.use(GAuth, gauthOption)



const VueCookie = require('vue-cookie');
Vue.use(VueCookie);


const init = async () => {
  if (Vue.prototype.$IS_APP) {
    try {
      const { value } = await Device.getLanguageCode();
      if (value) {
        const shortLang = value.substring(0, 2).toLowerCase();
        if (i18n.messages[shortLang]) {
          i18n.locale = shortLang;
        } else {
          i18n.locale = 'it';
        }
      }
    } catch (e) {
      console.warn('Error getting device language', e);
    }
  }

  // Mount app created by createApp()
  vueApp.$mount('#app')
};

init();


export const router = vRouter
